import { useState, useEffect } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import type { ColumnOrderState, ColumnPinningState } from '@tanstack/react-table';
import type { ColumnVisibilityState, ColumnSizingState } from './tableSettings';
import { serializeTableSettings } from './tableSettings';

/**
 * Tracks whether the current column state (visibility, order, pinning, sizing) differs
 * from the column state saved in a view (_ts string).
 *
 * Subscribes to the four localStorage keys so it reacts to column changes in Table.tsx.
 * Only triggers a ViewDropdown re-render when the dirty boolean flips (false → true or
 * true → false), not on every intermediate resize event.
 */
export const useColumnStateChanged = (tableName: string, savedTs: string): boolean => {
  const [columnVisibility] = useLocalStorageState<ColumnVisibilityState>(
    `${tableName}-tableColumnVisibility`,
    { defaultValue: {} }
  );
  const [columnOrder] = useLocalStorageState<ColumnOrderState>(`${tableName}-tableColumnOrder`, {
    defaultValue: [],
  });
  const [columnPinning] = useLocalStorageState<ColumnPinningState>(
    `${tableName}-tableColumnPinned`,
    { defaultValue: { left: [], right: [] } }
  );
  const [columnSizing] = useLocalStorageState<ColumnSizingState>(`${tableName}-tableColumnSizing`, {
    defaultValue: {},
  });

  const [hasChanged, setHasChanged] = useState(false);

  // Re-evaluate whenever any column key changes.
  // Use the React state values directly (not a fresh localStorage read) so the
  // comparison uses exactly the same values that triggered the effect — this
  // avoids a window where savedTs has already advanced to the new view's _ts
  // but the localStorage write from setColumnVisibility hasn't landed yet.
  // Also reset to false when no view is selected so deselecting a view doesn't
  // leave savedHasChanged=true indefinitely.
  useEffect(() => {
    if (!savedTs) {
      setHasChanged(false);
      return;
    }
    const currentTs = serializeTableSettings({
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
    });
    setHasChanged(currentTs !== savedTs);
  }, [tableName, savedTs, columnVisibility, columnOrder, columnPinning, columnSizing]);

  return hasChanged;
};
